import { motion } from "framer-motion";
import { Particles } from "@tsparticles/react";
import { ArrowRightIcon, PlayIcon } from "@heroicons/react/24/outline";
import { heroParticlesConfig } from "../config/particles";
import { cn } from "../utils/cn";
import { useRef, useEffect, useState } from "react";
import { useSettings } from "../contexts/SettingsContext";
import { EXTERNAL_LINKS } from "../constants/links";

export default function Hero() {
  const { settings } = useSettings();
  const [ethUsd, setEthUsd] = useState<number | null>(null);
  const [connections, setConnections] = useState<
    Array<{ d: string; id: string }>
  >([]);
  const walletRef = useRef<HTMLDivElement>(null);
  const priceRef = useRef<HTMLDivElement>(null);
  const swapRef = useRef<HTMLDivElement>(null);
  const telegramRef = useRef<HTMLDivElement>(null);
  const workflowRef = useRef<HTMLDivElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);
  const ifelseRef = useRef<HTMLDivElement>(null);
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const calculateConnections = () => {
    const boxes = [
      { ref: walletRef, id: "wallet" },
      { ref: priceRef, id: "price" },
      { ref: swapRef, id: "swap" },
      { ref: telegramRef, id: "telegram" },
      { ref: workflowRef, id: "workflow" },
      { ref: alertRef, id: "alert" },
      { ref: ifelseRef, id: "ifelse" },
    ];

    const positions: Record<
      string,
      { x: number; y: number; width: number; height: number }
    > = {};

    boxes.forEach(({ ref, id }) => {
      if (ref.current) {
        const rect = ref.current?.getBoundingClientRect();
        if (rect) {
          positions[id] = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
            width: rect.width,
            height: rect.height,
          };
        } else {
          // Skip if ref is null
        }
      } else {
        // Skip if ref is null
      }
    });

    const newConnections: Array<{ d: string; id: string }> = [];

    // Helper function to create sine wave path
    const createSineWavePath = (
      x1: number,
      y1: number,
      x2: number,
      y2: number,
      amplitude: number = 30,
      frequency: number = 2
    ) => {
      const dx = x2 - x1;
      const dy = y2 - y1;
      const length = Math.sqrt(dx * dx + dy * dy);
      const steps = Math.max(20, Math.floor(length / 20)); // More steps for smoother waves

      let path = `M ${x1} ${y1}`;

      for (let i = 1; i <= steps; i++) {
        const t = i / steps;
        const x = x1 + dx * t;
        const y = y1 + dy * t;

        // Add sine wave perpendicular to the line direction
        const perpX = -dy / length; // Perpendicular X component
        const perpY = dx / length; // Perpendicular Y component
        const sineOffset =
          Math.sin(t * Math.PI * frequency) * amplitude * Math.sin(t * Math.PI); // Sine wave with envelope

        const waveX = x + perpX * sineOffset;
        const waveY = y + perpY * sineOffset;

        path += ` L ${waveX} ${waveY}`;
      }

      return path;
    };

    // Only create connections if we have visible boxes
    if (positions.wallet && positions.price) {
      newConnections.push({
        id: "wallet-price",
        d: createSineWavePath(
          positions.wallet.x,
          positions.wallet.y,
          positions.price.x,
          positions.price.y,
          60,
          2
        ),
      });
    }

    if (positions.price && positions.telegram) {
      newConnections.push({
        id: "price-telegram",
        d: createSineWavePath(
          positions.price.x,
          positions.price.y,
          positions.telegram.x,
          positions.telegram.y,
          70,
          1.5
        ),
      });
    }

    // NEW: Wallet Connect to IfElse
    if (positions.wallet && positions.ifelse) {
      newConnections.push({
        id: "wallet-ifelse",
        d: createSineWavePath(
          positions.wallet.x,
          positions.wallet.y,
          positions.ifelse.x,
          positions.ifelse.y,
          80,
          2.5
        ),
      });
    }

    // NEW: IfElse to Swap
    if (positions.ifelse && positions.swap) {
      newConnections.push({
        id: "ifelse-swap",
        d: createSineWavePath(
          positions.ifelse.x,
          positions.ifelse.y,
          positions.swap.x,
          positions.swap.y,
          90,
          1.8
        ),
      });
    }

    // Additional connections for workflow elements
    if (positions.workflow && positions.price) {
      newConnections.push({
        id: "price-workflow",
        d: createSineWavePath(
          positions.price.x,
          positions.price.y,
          positions.workflow.x,
          positions.workflow.y,
          50,
          1.2
        ),
      });
    }

    if (positions.alert && positions.swap) {
      newConnections.push({
        id: "alert-swap",
        d: createSineWavePath(
          positions.alert.x,
          positions.alert.y,
          positions.swap.x,
          positions.swap.y,
          60,
          1.8
        ),
      });
    }

    // Add debugging and fallback connections
    if (newConnections.length === 0) {
      newConnections.push(
        {
          id: "fallback-1",
          d: createSineWavePath(200, 150, 600, 200, 70, 2),
        },
        {
          id: "fallback-2",
          d: createSineWavePath(600, 200, 1000, 250, 80, 1.5),
        },
        {
          id: "fallback-3",
          d: createSineWavePath(1000, 250, 600, 350, 90, 2.2),
        },
        {
          id: "fallback-4",
          d: createSineWavePath(600, 350, 200, 300, 60, 1.8),
        }
      );
    }

    setConnections(newConnections);
  };

  useEffect(() => {
    // Calculate connections after components mount
    const timer = setTimeout(calculateConnections, 1000);

    // Add resize listener
    const handleResize = () => {
      setTimeout(calculateConnections, 100);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Recalculate when animations complete
  useEffect(() => {
    const timer = setTimeout(calculateConnections, 4000); // After all animations
    return () => clearTimeout(timer);
  }, []);

  // Fetch ETH price once on mount (no polling)
  useEffect(() => {
    let isCancelled = false;
    const fetchEthPrice = async () => {
      try {
        const res = await fetch(
          "https://api.coinbase.com/v2/prices/ETH-USD/spot"
        );
        if (res.ok) {
          const data: { data: { amount: string } } = await res.json();
          const price = parseFloat(data?.data?.amount);
          if (!isCancelled && Number.isFinite(price)) {
            setEthUsd(price);
            return;
          }
        }
      } catch {}

      try {
        const res2 = await fetch(
          "https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd"
        );
        if (res2.ok) {
          const data2: { ethereum?: { usd?: number } } = await res2.json();
          const price2 = data2?.ethereum?.usd;
          if (!isCancelled && typeof price2 === "number") {
            setEthUsd(price2);
          }
        }
      } catch {}
    };

    fetchEthPrice();
    return () => {
      isCancelled = true;
    };
  }, []);
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const floatingVariants = {
    animate: {
      y: [-10, 10, -10],
      rotate: [0, 2, -2, 0],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      role="banner"
    >
      {/* Top fade to previous section */}
      <div className="pointer-events-none absolute inset-x-0 -top-16 h-16 bg-gradient-to-b from-transparent to-white/80 dark:to-[#0b0f19]/80 [mask-image:linear-gradient(to_bottom,black,transparent)]" />
      {/* Particle Background */}
      <div className="absolute inset-0">
        <Particles
          id="hero-particles"
          options={heroParticlesConfig}
          className="absolute inset-0"
        />
      </div>

      {/* Simple overlay */}
      <div className="absolute inset-0 bg-gray-50/80 dark:bg-white/5" />

      {/* Dotted Grid Pattern */}
      <div className="absolute inset-0 opacity-40 dark:opacity-25 dotted-grid" />

      {/* Secondary Dotted Grid for depth */}
      <div className="absolute inset-0 opacity-20 dark:opacity-15 dotted-grid-secondary" />

      {/* DApp Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Dynamic Connection Lines Between DApp Boxes */}
        <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 0 }}>
          <defs>
            <linearGradient
              id="lineGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="rgba(233, 92, 73, 0.3)" />
              <stop offset="50%" stopColor="rgba(233, 92, 73, 0.3)" />
              <stop offset="100%" stopColor="rgba(233, 92, 73, 0.3)" />
            </linearGradient>
            <linearGradient
              id="lineGradient2"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="rgba(233, 92, 73, 0.2)" />
              <stop offset="50%" stopColor="rgba(233, 92, 73, 0.2)" />
              <stop offset="100%" stopColor="rgba(233, 92, 73, 0.2)" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="1" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Dynamic Connections Based on Actual Box Positions */}
          {connections.map((connection, index) => (
            <motion.path
              key={connection.id}
              d={connection.d}
              stroke={
                index % 2 === 0 ? "url(#lineGradient)" : "url(#lineGradient2)"
              }
              strokeWidth="1.5"
              fill="none"
              filter="url(#glow)"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 0.4 }}
              transition={{
                duration: 2 + index * 0.3,
                delay: 0.5 + index * 0.3,
                ease: "easeInOut",
              }}
            />
          ))}

          {/* Animated Data Flow Points */}
          {[...Array(6)].map((_, i) => (
            <motion.circle
              key={i}
              r="2"
              fill="rgba(233, 92, 73, 0.8)"
              filter="url(#glow)"
              initial={{ opacity: 0 }}
              animate={{
                opacity: [0, 1, 0],
                cx: [100 + i * 150, 200 + i * 120, 300 + i * 100],
                cy: [200 + i * 60, 400 - i * 40, 600 - i * 50],
              }}
              transition={{
                duration: 8,
                delay: i * 0.8,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}
        </svg>

        {/* Mobile DApp Elements - Simplified for smaller screens */}
        <div className="absolute inset-0 lg:hidden">
          {/* Mobile floating elements */}
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{
                left: `${10 + i * 30}%`,
                top: `${20 + i * 25}%`,
              }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0.2, 0.6, 0.2],
                scale: [0.5, 1, 0.5],
                y: [-5, 5, -5],
              }}
              transition={{
                duration: 2 + i * 0.5,
                repeat: Infinity,
                delay: i * 0.4,
                ease: "easeInOut",
              }}
            >
              <div
                className="w-3 h-3 rounded-full shadow-lg"
                style={{
                  backgroundColor: "#e95c49",
                  boxShadow: "0 0 10px rgba(233, 92, 73, 0.5)",
                }}
              ></div>
            </motion.div>
          ))}
        </div>

        {/* Floating DApp Node Cards */}
        <motion.div
          ref={walletRef}
          className="absolute top-20 left-10 hidden lg:block"
          initial={{ opacity: 0, scale: 0.8, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
        >
          <motion.div
            className="glass-effect p-4 rounded-xl border border-primary-400/20 shadow-lg shadow-primary-400/10 max-w-xs opacity-25"
            animate={{
              y: [-5, 5, -5],
              rotateY: [0, 2, -2, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: "#e95c49" }}
              >
                <svg
                  className="w-4 h-4 text-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                Wallet Connect
              </span>
            </div>
            <div className="space-y-2">
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Chain: Ethereum
              </div>
              <div className="text-xs font-mono bg-black/10 dark:bg-white/10 px-2 py-1 rounded">
                0xfd8...7d49
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                <span className="text-xs text-green-400">Connected</span>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          ref={priceRef}
          className="absolute top-32 right-20 hidden lg:block"
          initial={{ opacity: 0, scale: 0.8, y: -50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.6 }}
        >
          <motion.div
            className="glass-effect p-4 rounded-xl border border-lightPurple-400/20 shadow-lg shadow-lightPurple-400/10 max-w-xs opacity-25"
            animate={{
              y: [3, -3, 3],
              rotateX: [0, -1, 1, 0],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: "#e95c49" }}
              >
                <svg
                  className="w-4 h-4 text-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
              </div>
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                Price Feed
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  ETH/USD
                </span>
                <span className="text-sm font-bold text-green-400">
                  {ethUsd !== null
                    ? `$${ethUsd.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`
                    : "—"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  24h Change
                </span>
                <span className="text-xs text-green-400">+3.2%</span>
              </div>
              <motion.div
                className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 1, delay: 2 }}
              >
                <div
                  className="h-1 rounded-full w-3/4"
                  style={{ backgroundColor: "#e95c49" }}
                ></div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          ref={swapRef}
          className="absolute bottom-32 left-20 hidden lg:block"
          initial={{ opacity: 0, scale: 0.8, x: -50 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 2 }}
          style={{ zIndex: 1, left: "50px", bottom: "50px" }}
        >
          <motion.div
            className="glass-effect p-4 rounded-xl border border-purple-400/20 shadow-lg shadow-purple-400/10 max-w-sm backdrop-blur-xl opacity-25"
            animate={{
              y: [-2, 4, -2],
              rotateZ: [0, 1, -1, 0],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: "#e95c49" }}
              >
                <svg
                  className="w-4 h-4 text-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                  />
                </svg>
              </div>
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                Swap
              </span>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                  From
                </div>
                <div className="bg-black/5 dark:bg-white/5 p-2 rounded">
                  <div className="text-sm font-semibold">ETH</div>
                  <div className="text-sm">1.0</div>
                </div>
              </div>
              <div className="flex items-center justify-center">
                <motion.div
                  animate={{ rotateZ: 180 }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "reverse",
                  }}
                  className="w-6 h-6 text-gray-400"
                >
                  →
                </motion.div>
              </div>
              <div className="flex-1">
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                  To
                </div>
                <div className="bg-black/5 dark:bg-white/5 p-2 rounded">
                  <div className="text-sm font-semibold">USDC</div>
                  <div className="text-sm text-green-400">
                    {ethUsd !== null
                      ? ethUsd.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "—"}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          ref={telegramRef}
          className="absolute bottom-20 right-32 hidden lg:block"
          initial={{ opacity: 0, scale: 0.8, x: 50 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 2.4 }}
        >
          <motion.div
            className="glass-effect p-4 rounded-xl border border-green-400/20 shadow-lg shadow-green-400/10 max-w-xs opacity-25"
            animate={{
              y: [4, -2, 4],
              rotateY: [0, -2, 2, 0],
            }}
            transition={{
              duration: 4.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: "#e95c49" }}
              >
                <svg
                  className="w-4 h-4 text-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
              </div>
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                Telegram Notify
              </span>
            </div>
            <div className="space-y-2">
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Chat ID: @defi_alerts
              </div>
              <div className="text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-2 py-1 rounded">
                ✅ Price:{" "}
                {ethUsd !== null
                  ? `$${ethUsd.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`
                  : "—"}
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                <span className="text-xs text-green-400">Active</span>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Floating Data Points */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute hidden lg:block"
            style={{
              left: `${20 + i * 15}%`,
              top: `${15 + i * 12}%`,
            }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: [0.3, 0.8, 0.3],
              scale: [0.8, 1.2, 0.8],
              y: [-10, 10, -10],
            }}
            transition={{
              duration: 3 + i * 0.5,
              repeat: Infinity,
              delay: i * 0.3,
              ease: "easeInOut",
            }}
          >
            <div
              className="w-2 h-2 rounded-full shadow-lg"
              style={{
                backgroundColor: "#e95c49",
                boxShadow: "0 0 10px rgba(233, 92, 73, 0.5)",
              }}
            ></div>
          </motion.div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Main Headline */}
          <motion.div variants={itemVariants} className="space-y-4">
            <motion.h1
              className="text-4xl sm:text-6xl lg:text-7xl font-oxanium font-bold leading-tight"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <span className="block" style={{ color: settings.primaryColor }}>
                {settings.heroTitle.split(" ").slice(0, 2).join(" ")}
              </span>
              <span className="block" style={{ color: settings.primaryColor }}>
                {settings.heroTitle.split(" ").slice(2).join(" ")}
              </span>
            </motion.h1>
          </motion.div>

          {/* Subheading */}
          <motion.p
            variants={itemVariants}
            className="max-w-3xl mx-auto text-lg sm:text-xl lg:text-2xl text-gray-600 dark:text-gray-300 leading-relaxed"
          >
            {settings.heroSubtitle}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <motion.a
              href={EXTERNAL_LINKS.beta}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "group relative px-8 py-4 rounded-xl font-semibold text-lg",
                "text-white",
                "shadow-lg hover:shadow-xl",
                "transition-all duration-300 glow-button",
                "flex items-center gap-3"
              )}
              style={{
                backgroundColor: settings.heroButtonColor,
                boxShadow: `0 10px 25px ${settings.heroButtonColor}40`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  settings.heroButtonColor;
                e.currentTarget.style.boxShadow = `0 20px 40px ${settings.heroButtonColor}66`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  settings.heroButtonColor;
                e.currentTarget.style.boxShadow = `0 10px 25px ${settings.heroButtonColor}40`;
              }}
              whileHover={{
                scale: 1.05,
                boxShadow: "0 20px 40px rgba(233, 92, 73, 0.4)",
              }}
              whileTap={{ scale: 0.98 }}
            >
              {settings.heroButtonText}
              <motion.div
                className="flex items-center"
                initial={{ x: 0 }}
                whileHover={{ x: 5 }}
                transition={{ duration: 0.2 }}
              >
                <ArrowRightIcon className="h-5 w-5" />
              </motion.div>
              {/* Glow effect */}
              <div
                className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                style={{ backgroundColor: "#e95c49" }}
              />
            </motion.a>

            <motion.button
              onClick={() => setIsVideoOpen(true)}
              className={cn(
                "group px-8 py-4 rounded-xl font-semibold text-lg",
                "bg-white/80 dark:bg-white/10 text-gray-900 dark:text-white",
                "border border-gray-200/50 dark:border-white/20",
                "hover:bg-white/90 dark:hover:bg-white/20",
                "hover:border-gray-300/50 dark:hover:border-white/30",
                "backdrop-blur-sm transition-all duration-300",
                "flex items-center gap-3"
              )}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <PlayIcon className="h-5 w-5" />
              Watch Demo
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Enhanced Floating Workflow Dashboard - Between Price Feed and Telegram */}
        <motion.div
          ref={workflowRef}
          variants={floatingVariants}
          animate="animate"
          className="absolute top-1/2 transform -translate-y-1/2 hidden xl:block"
          style={{ zIndex: 1, right: "-250px", top: "150px" }}
        >
          <motion.div
            className="glass-effect p-6 rounded-2xl border border-primary-400/20 shadow-lg shadow-primary-400/10 max-w-sm backdrop-blur-xl opacity-30"
            initial={{ opacity: 0, scale: 0.9, rotateY: -15 }}
            animate={{ opacity: 0.3, scale: 1, rotateY: 0 }}
            transition={{ duration: 1, delay: 2.8 }}
            whileHover={{
              scale: 1.02,
              boxShadow: "0 25px 50px rgba(233, 92, 73, 0.4)",
            }}
          >
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <motion.div
                    className="w-3 h-3 rounded-full bg-green-400"
                    animate={{ scale: [1, 1.3, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    Workflow Active
                  </span>
                </div>
                <div className="text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
                  Live
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    DCA Strategy
                  </span>
                  <motion.span
                    className="text-green-400 font-bold text-lg"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    +12.5%
                  </motion.span>
                </div>
                <div className="w-full bg-gray-200/50 dark:bg-gray-700/50 rounded-full h-3 overflow-hidden">
                  <motion.div
                    className="h-3 rounded-full relative"
                    style={{ backgroundColor: "#e95c49" }}
                    initial={{ width: "60%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 2, delay: 1.5 }}
                  >
                    <motion.div
                      className="absolute inset-0 bg-white/30 rounded-full"
                      animate={{ x: [-100, 200] }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    />
                  </motion.div>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500 dark:text-gray-400">
                    Progress
                  </span>
                  <span className="text-gray-600 dark:text-gray-300 font-medium">
                    75%
                  </span>
                </div>
              </div>

              <div className="pt-3 border-t border-gray-200/30 dark:border-gray-700/30">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Next execution
                  </span>
                  <motion.span
                    className="text-xs text-primary-400 font-medium"
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    2h 15m
                  </motion.span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Price Alert Notification - Bottom between Telegram and Swap */}
        <motion.div
          ref={alertRef}
          initial={{ opacity: 0, x: -100, scale: 0.8 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ duration: 0.8, delay: 2 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden xl:block"
          style={{ zIndex: 1, bottom: "-200px", left: "300px" }}
        >
          <motion.div
            className="glass-effect p-4 rounded-xl border border-green-400/20 shadow-lg shadow-green-400/10 max-w-xs backdrop-blur-xl opacity-30"
            whileHover={{
              scale: 1.05,
              boxShadow: "0 20px 40px rgba(34, 197, 94, 0.3)",
            }}
            animate={{
              y: [-2, 2, -2],
            }}
            transition={{
              y: { duration: 3, repeat: Infinity, ease: "easeInOut" },
            }}
          >
            <div className="flex items-start gap-3">
              <motion.div
                className="w-2 h-2 rounded-full bg-green-400 mt-2"
                animate={{
                  scale: [1, 1.5, 1],
                  opacity: [0.7, 1, 0.7],
                }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
              <div className="space-y-2">
                <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  🎯 Price Alert Triggered
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                  ETH reached $2,500 - Limit order executed successfully
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <div className="text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-1 rounded">
                    ✅ Executed
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    2 min ago
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Additional IfElse Function Card */}
        <motion.div
          ref={ifelseRef}
          className="absolute top-20 left-1/4 hidden xl:block"
          initial={{ opacity: 0, scale: 0.8, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 2.8 }}
          style={{ zIndex: 1, left: "-300px", top: "150px" }}
        >
          <motion.div
            className="glass-effect p-4 rounded-xl border border-orange-400/20 shadow-lg shadow-orange-400/10 max-w-xs backdrop-blur-xl opacity-30"
            animate={{
              y: [2, -4, 2],
              rotateX: [0, 1, -1, 0],
            }}
            transition={{
              duration: 5.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: "#e95c49" }}
              >
                <svg
                  className="w-4 h-4 text-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                IfElse Function
              </span>
            </div>
            <div className="space-y-2">
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Condition: Price &gt; $2,500
              </div>
              <div className="flex items-center justify-between bg-black/5 dark:bg-white/5 p-2 rounded">
                <span className="text-xs">If True</span>
                <motion.div
                  className="w-3 h-3 rounded-full bg-green-400"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                />
              </div>
              <div className="flex items-center justify-between bg-black/5 dark:bg-white/5 p-2 rounded">
                <span className="text-xs">If False</span>
                <div className="w-3 h-3 rounded-full bg-gray-400"></div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom fade to next section */}
      <div className="pointer-events-none absolute inset-x-0 -bottom-16 h-16 bg-gradient-to-t from-transparent to-white/80 dark:to-[#0b0f19]/80 [mask-image:linear-gradient(to_top,black,transparent)]" />

      {/* Video Modal */}
      {isVideoOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/70"
            onClick={() => setIsVideoOpen(false)}
          />
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black">
              <button
                aria-label="Close"
                className="absolute top-3 right-3 z-20 rounded-full bg-white/10 text-white hover:bg-white/20 px-3 py-1"
                onClick={() => setIsVideoOpen(false)}
              >
                ✕
              </button>
              <video
                className="w-full h-auto"
                controls
                autoPlay
                poster="/seeinaction.png"
              >
                <source src="/OL.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      )}

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2-translate-x-1/2 z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.5 }}
      >
        <motion.div
          className="w-6 h-10 border-2 border-gray-400 dark:border-gray-600 rounded-full flex justify-center"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <motion.div
            className="w-1 h-3 bg-gray-400 dark:bg-gray-600 rounded-full mt-2"
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
}
